import { useEffect, useState } from "react";
import { Check, X, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";

const TABS = ["pending", "approved", "rejected"];

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("pending");

  const load = () => api.get(`/admin/reviews?status=${tab}`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { action });
      toast.success(`Review ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Review deleted");
    load();
  };

  return (
    <div data-testid="admin-reviews">
      <PageTitle title="Reviews & Testimonials" sub="Approve what the public sees. Feature the best ones." />
      <div className="flex gap-2 mb-6" data-testid="reviews-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`reviews-tab-${t}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors duration-150 ${tab === t ? "bg-blurple text-white" : "border border-white/15 text-white/50 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {reviews.length === 0 ? (
        <p className="text-white/40 text-sm font-mono" data-testid="reviews-empty">No {tab} reviews.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-lg border border-white/5 bg-panel p-5" data-testid={`review-admin-${r.username}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white text-sm">{r.username}</span>
                  {r.featured && <Badge tone="cyan">featured</Badge>}
                </div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={13} className={i <= r.rating ? "text-amber-400" : "text-white/15"} fill={i <= r.rating ? "#F59E0B" : "none"} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-white/70 leading-relaxed mb-4">“{r.text}”</p>
              <div className="flex gap-2 flex-wrap">
                {r.status !== "approved" && (
                  <button onClick={() => act(r.id, "approve")} data-testid={`review-approve-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-full px-3 py-1.5 transition-colors duration-150"><Check size={12} /> Approve</button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => act(r.id, "reject")} data-testid={`review-reject-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 border border-amber-400/30 hover:bg-amber-400/10 rounded-full px-3 py-1.5 transition-colors duration-150"><X size={12} /> Reject</button>
                )}
                <button onClick={() => act(r.id, "feature")} data-testid={`review-feature-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-volt border border-volt/30 hover:bg-volt/10 rounded-full px-3 py-1.5 transition-colors duration-150"><Star size={12} /> {r.featured ? "Unfeature" : "Feature"}</button>
                <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-full px-3 py-1.5 transition-colors duration-150"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
